import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, Clock, User, ChevronRight, CheckCircle, ThumbsUp, ThumbsDown } from 'lucide-react';

const BOOKING_STATUS_CONFIG = {
  confirmed: { label: 'Confirmed', className: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', className: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
  no_show: { label: 'No Show', className: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In Progress', className: 'bg-violet-100 text-violet-700' }
};

const BookingsListLite = ({ bookings = [], onCheckIn, onApprove, onReject, onView, loading = false }) => {
  const navigate = useNavigate();

  const formatBookingDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return 'Today';
      if (isTomorrow(date)) return 'Tomorrow';
      return format(date, 'MMM d');
    } catch {
      return dateStr;
    }
  };

  const formatBookingTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      // Handle various time formats
      if (timeStr.includes('T')) {
        return format(parseISO(timeStr), 'h:mm a');
      }
      // Simple time string like "14:00"
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const getStatusConfig = (status) => {
    return BOOKING_STATUS_CONFIG[status?.toLowerCase()] || { label: status || 'Scheduled', className: 'bg-gray-100 text-gray-600' };
  };

  const getCustomerName = (booking) => {
    return booking.customer?.name || booking.customer_name || booking.client_name || 'Customer';
  };

  const getServiceName = (booking) => {
    return booking.service?.title || booking.service_name || booking.listing?.title || booking.title || 'Service';
  };

  const canCheckIn = (booking) => {
    const status = booking.status?.toLowerCase();
    if (status === 'completed' || status === 'cancelled' || status === 'no_show') return false;
    
    // Allow check-in for today's confirmed bookings
    if (status === 'confirmed') {
      const bookingDate = booking.booking_date || booking.date || booking.scheduled_date;
      if (bookingDate) {
        try {
          return isToday(parseISO(bookingDate));
        } catch {
          return false;
        }
      }
    }
    return false;
  };

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i.id || i.name || `${i.title || "i"}-${i}`} className="bg-white p-4 rounded-xl animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
            <div className="h-5 bg-gray-200 rounded w-1/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!bookings.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-white">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Calendar className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-900 font-medium text-base mb-1">No bookings yet</p>
        <p className="text-gray-500 text-sm">Service bookings will appear here</p>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="bookings-list-lite">
      {bookings.map((booking, index) => {
        const statusConfig = getStatusConfig(booking.status);
        const bookingDate = booking.booking_date || booking.date || booking.scheduled_date;
        const bookingTime = booking.booking_time || booking.time || booking.start_time;
        const showCheckIn = canCheckIn(booking);
        
        return (
          <div 
            key={booking.id || booking._id}
            className={`p-4 active:bg-gray-50 transition-colors cursor-pointer ${
              index !== bookings.length - 1 ? 'border-b border-gray-100' : ''
            }`}
            onClick={() => navigate(`/bookings/${booking.id || booking._id}`)}
            data-testid={`booking-item-${booking.id || booking._id}`}
          >
            {/* Top Row: Date/Time & Status */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-indigo-600">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm font-semibold">
                    {formatBookingDate(bookingDate)}
                  </span>
                </div>
                {bookingTime && (
                  <div className="flex items-center gap-1 text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs">
                      {formatBookingTime(bookingTime)}
                    </span>
                  </div>
                )}
              </div>
              <Badge className={`text-[10px] px-2 py-0.5 font-medium rounded-full ${statusConfig.className}`}>
                {statusConfig.label}
              </Badge>
            </div>
            
            {/* Middle: Service & Customer */}
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                {getServiceName(booking)}
              </p>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                <User className="h-3 w-3 text-gray-400" />
                <span className="truncate max-w-[200px]">{getCustomerName(booking)}</span>
              </div>
            </div>
            
            {/* Bottom: Action */}
            <div className="flex items-center justify-end gap-2">
              {booking.status === 'pending' && onApprove && onReject ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs font-medium text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                    onClick={(e) => { e.stopPropagation(); onReject(booking); }}
                    data-testid={`reject-booking-lite-${booking.id}`}
                  >
                    <ThumbsDown className="h-3.5 w-3.5 mr-1.5" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 px-4 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 rounded-full"
                    onClick={(e) => { e.stopPropagation(); onApprove(booking); }}
                    data-testid={`approve-booking-lite-${booking.id}`}
                  >
                    <ThumbsUp className="h-3.5 w-3.5 mr-1.5" />
                    Approve
                  </Button>
                </>
              ) : showCheckIn && onCheckIn ? (
                <Button 
                  size="sm"
                  className="h-8 px-4 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCheckIn(booking);
                  }}
                  data-testid={`checkin-booking-${booking.id}`}
                >
                  <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                  Check-in
                </Button>
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-300" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BookingsListLite;
